// E8QuadrilateralAnglesClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 11) – katy wewnetrzne czworokata.
// beta = alfa + d1, gamma = k*alfa, delta = 90 (kat prosty). Suma = 360.
// alfa = (360 - 90 - d1)/(2 + k), pytamy o beta = alfa + d1.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const DELTA = 90;

// (d1, k) dajace calkowite alfa, beta < 180
const COMBOS = [
  { d1: 70, k: 2 }, { d1: 50, k: 2 }, { d1: 30, k: 2 },
  { d1: 50, k: 3 }, { d1: 60, k: 3 }, { d1: 70, k: 3 }, { d1: 40, k: 3 },
];

const K_WORD = { 2: "dwukrotnie", 3: "trzykrotnie" };

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildData({ d1, k, fixedOptions = null }) {
  const alpha = (360 - DELTA - d1) / (2 + k);
  const beta = alpha + d1;
  const gamma = k * alpha;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(beta);
  } else {
    const pool = [beta, alpha, gamma, beta + 20, beta - 20, alpha + gamma];
    const unique = [];
    pool.forEach((v) => {
      if (v > 0 && v < 360 && !unique.includes(v)) unique.push(v);
    });
    const distractors = unique.filter((v) => v !== beta).slice(0, 3);
    const ordered = shuffle([beta, ...distractors]);
    options = ordered.map((value, index) => ({ value, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(beta);
  }

  return { d1, k, alpha, beta, gamma, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8QuadrilateralAnglesClosedTask",
    data: buildData({ d1: 70, k: 2, fixedOptions: [50, 85, 120, 155] }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 11,
    },
    tags: ["e8", "planimetria", "czworokaty", "katy"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const { d1, k } = pick(COMBOS);
  return {
    type: "E8QuadrilateralAnglesClosedTask",
    data: buildData({ d1, k }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_11" },
    tags: ["e8", "planimetria", "czworokaty", "katy"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Kąty wewnętrzne czworokąta oznaczono: <InlineMath math="\alpha" />,{" "}
        <InlineMath math="\beta" />, <InlineMath math="\gamma" /> oraz{" "}
        <InlineMath math="\delta" />. Miara kąta <InlineMath math="\beta" /> jest o{" "}
        <InlineMath math={`${data.d1}^{\\circ}`} /> większa od miary kąta{" "}
        <InlineMath math="\alpha" />, miara kąta <InlineMath math="\gamma" /> jest{" "}
        {K_WORD[data.k]} większa od miary kąta <InlineMath math="\alpha" />. Kąt{" "}
        <InlineMath math="\delta" /> jest kątem prostym.
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Miara kąta <InlineMath math="\beta" /> jest równa
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}^{\\circ}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.beta}^{\\circ}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}^{\\circ}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const { d1, k, alpha, beta, gamma } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Suma kątów wewnętrznych czworokąta to{" "}
        <InlineMath math="360^{\circ}" />. Zapisujemy równanie (kąt prosty{" "}
        <InlineMath math="\delta=90^{\circ}" />):
      </p>
      <BlockMath math={`\\alpha+(\\alpha+${d1})+${k}\\alpha+90=360`} />
      <p>
        <strong>Krok 2.</strong> Rozwiązujemy:
      </p>
      <BlockMath
        math={`${2 + k}\\alpha=360-90-${d1}=${360 - 90 - d1}\\ \\Rightarrow\\ \\alpha=${alpha}^{\\circ}`}
      />
      <p>
        <strong>Krok 3.</strong> Obliczamy{" "}
        <InlineMath math={`\\beta=\\alpha+${d1}=${alpha}+${d1}=${beta}^{\\circ}`} />.{" "}
        (Sprawdzenie: <InlineMath math={`\\gamma=${gamma}^{\\circ}`} />, suma{" "}
        <InlineMath math={`${alpha}+${beta}+${gamma}+90=360`} />.)
      </p>
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
